/* eslint-disable max-nested-callbacks */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  Modal,
  INPUT_THEME,
  CheckBox,
  Button,
  BUTTON_THEME,
  Card,
  CARD_THEME,
  Pagination,
  Input,
} from '@st-operations/uikit/src/v2/index.js'

import './styles.scss'

const MODAL_STYLES = {
  content: 'uup-multi-select-grouped-modal__container',
  childWrapper: 'uup-multi-select-grouped-modal__wrapper',
  innerWrapper: 'uup-multi-select-grouped-modal__inner-wrapper',
  headerClose: 'uup-multi-select-grouped-modal__close-button',
}

const PAGE_SIZE = 50

/**
 * A modal component that displays grouped options with multi-select functionality
 *
 * @param {Object} props - Component props
 *
 * @param {string} props.title - The title of the modal
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {boolean} props.loading - Loading state of the modal
 *
 * @param {Object} props.availableOptionsGrouped - A map of available options grouped by category
 * @param {Array} props.selectedOptions - Array of currently selected options
 *
 * @param {Function} props.extractValue - Function to extract value from option
 * @param {Function} props.extractLabel - Function to extract label from option
 * @param {Function} props.extractGroupValue - Function to extract group value from option
 *
 * @param {Function} props.onSubmit - Callback function when the selection is submitted
 *
 * @param {boolean} props.hideAllFlag - Hide the "All Flag" checkbox
 * @param {boolean} props.hideShowValue - Hide the "Show Value" checkbox
 * @param {boolean} props.hideShowOnlySelectedGroups - Hide the "Show Only Selected Groups" checkbox
 */
const MultiSelectGroupedModal = ({
  title = 'Select',
  open = false,
  onClose = () => {},
  loading = false,
  optionsGrouped = {},
  selectedOptions: defaultSelectedOptions = [],
  extractValue = (option) => option?.value,
  extractLabel = (option) => option?.value,
  extractGroupKey = (option) => option?.group,
  onSubmit = () => {},
  hideAllFlag = false,
  hideShowValue = false,
  hideShowOnlySelectedGroups = true,
}) => {
  const [searchText, setSearchText] = useState('')
  const [groupSearchText, setGroupSearchText] = useState('')
  const [currentOptions, setCurrentOptions] = useState([])
  const [selectedCategoryValue, setSelectedGroupValue] = useState(null)
  const [currentSelectedOptions, setCurrentSelectedOptions] = useState([])

  const [showValue, setShowValue] = useState(false)
  const [showOnlySelected, setShowOnlySelected] = useState(false)
  const [showOnlySelectedGroups, setShowOnlySelectedGroups] = useState(false)

  const [offset, setOffset] = useState(0)

  const groupOptionsLabeled = useMemo(() => {
    const { all: allOptions, ...optionsGroupedWithoutAll } = optionsGrouped
    const groupOptionsLabeled = Object.keys(optionsGroupedWithoutAll || {})
      .map((categoryKey) => ({
        value: categoryKey,
        label: categoryKey,
      }))
      .sort((a, b) => (a.label >= b.label ? 1 : -1))

    const result = groupOptionsLabeled.filter((labeledGroupOption) => {
      const showOnlySelectedGroupsAllow = showOnlySelectedGroups
        ? currentSelectedOptions
            .map((option) => extractGroupKey(option))
            .includes(labeledGroupOption.value)
        : true
      const groupMatchsSearch = labeledGroupOption.label
        .toLowerCase()
        .includes(groupSearchText.toLowerCase())

      return showOnlySelectedGroupsAllow && groupMatchsSearch
    })

    if (allOptions) {
      result.unshift({
        value: 'all',
        label: 'All',
      })
    }

    return result
  }, [optionsGrouped, showOnlySelectedGroups, currentSelectedOptions, groupSearchText])

  const paginationOptionsLabeled = useMemo(
    () =>
      new Array(Math.ceil((optionsGrouped[selectedCategoryValue]?.length || 0) / PAGE_SIZE))
        .fill(null)
        .map((_, index) => ({ label: index + 1, value: index + 1 })),
    [optionsGrouped, selectedCategoryValue]
  )

  const allFlagSelected = useMemo(
    () =>
      currentSelectedOptions.length > 0 &&
      currentSelectedOptions.find((option) => extractValue(option) === 'all'),
    [currentSelectedOptions, currentOptions]
  )

  const countSelectedOptionsByCategory = useCallback(
    (categoryValue) => {
      const categoryOptions = optionsGrouped?.[categoryValue]
      if (!categoryOptions) return 0

      const result = categoryOptions.filter((option) =>
        currentSelectedOptions.find(
          (currentSelected) => extractValue(currentSelected) === extractValue(option)
        )
      )
      return result
    },
    [optionsGrouped, currentSelectedOptions]
  )

  const selectedCategoryOptionsCount = useMemo(
    () =>
      selectedCategoryValue !== 'all'
        ? countSelectedOptionsByCategory?.(selectedCategoryValue)?.length
        : currentSelectedOptions.length,
    [selectedCategoryValue, currentSelectedOptions, countSelectedOptionsByCategory]
  )

  useEffect(
    function refreshSelectedCategoryValue() {
      if (!selectedCategoryValue) {
        setSelectedGroupValue(optionsGrouped?.all ? 'all' : groupOptionsLabeled?.[0]?.value || null)
        return
      }
    },
    [groupOptionsLabeled, optionsGrouped]
  )

  useEffect(
    function refreshCurrentSelectedOptions() {
      setCurrentSelectedOptions(defaultSelectedOptions)
    },
    [defaultSelectedOptions]
  )

  useEffect(
    function resetOffset() {
      setOffset(0)
    },
    [searchText, selectedCategoryValue]
  )

  useEffect(
    function filterCurrentOptions() {
      const options = selectedCategoryValue
        ? optionsGrouped[selectedCategoryValue]
        : optionsGrouped.all

      if (!options) {
        setCurrentOptions([])
        return
      }

      const newOptions = options.filter((option) => {
        const searchTextMatch = extractLabel(option)
          .toLowerCase()
          .includes(searchText.toLowerCase())
        const onlySelectedMatch = showOnlySelected
          ? currentSelectedOptions.find(
              (currentSelectedOption) =>
                extractValue(currentSelectedOption) === extractValue(option)
            )
          : true

        return searchTextMatch && onlySelectedMatch
      })
      const newOptionsPaginated = newOptions.slice(offset, offset + PAGE_SIZE)
      setCurrentOptions(newOptionsPaginated)
    },
    [
      optionsGrouped,
      selectedCategoryValue,
      searchText,
      showOnlySelected,
      currentSelectedOptions,
      offset,
    ]
  )

  function handleSelectAll(checked) {
    if (checked) {
      setCurrentSelectedOptions([
        ...currentSelectedOptions.filter(
          (currSelected) =>
            !optionsGrouped[selectedCategoryValue].some(
              (option) => extractValue(option) === extractValue(currSelected)
            )
        ),
        ...optionsGrouped[selectedCategoryValue],
      ])
      return
    }

    setCurrentSelectedOptions([
      ...currentSelectedOptions.filter(
        (currSelected) =>
          !optionsGrouped[selectedCategoryValue].some(
            (option) => extractValue(option) === extractValue(currSelected)
          )
      ),
    ])
  }

  function handleAllFlag(checked) {
    setGroupSearchText('')
    setSearchText('')
    if (checked) {
      setCurrentSelectedOptions([
        {
          value: 'all',
          label: 'All',
        },
      ])
      return
    }

    setCurrentSelectedOptions([
      ...currentSelectedOptions.filter((currSelected) => extractValue(currSelected) !== 'all'),
    ])
  }

  function handleSearchChange(event) {
    setSearchText(event)
  }

  function handleGroupSearchChange(event) {
    setGroupSearchText(event)
  }

  function handleSelectedGroupChange(group) {
    setSelectedGroupValue(group.value)
    setSearchText('')
  }

  function handleShowValue() {
    setShowValue((prevState) => !prevState)
  }

  function handleOptionSelection(option) {
    const withoutOption = currentSelectedOptions.filter(
      (currentSelectedOption) => extractValue(currentSelectedOption) !== extractValue(option)
    )
    let newValue

    if (withoutOption.length === currentSelectedOptions.length) {
      newValue = [...currentSelectedOptions, option]
    } else {
      newValue = withoutOption
    }
    setCurrentSelectedOptions?.(newValue)
  }

  function handleShowSelected() {
    setShowOnlySelected((prevState) => !prevState)
  }

  function handleShowSelectedGroups() {
    setShowOnlySelectedGroups((prevState) => !prevState)
  }

  function handleSubmit() {
    onClose?.()
    onSubmit?.(currentSelectedOptions)
  }

  const handlePageChange = useCallback(({ value: { value: page } }) => {
    setOffset((page - 1) * PAGE_SIZE)
  }, [])

  return (
    <Modal isOpen={open} onClose={onClose} classes={MODAL_STYLES}>
      <section className="uup-multi-select-grouped-modal__content">
        <header className="uup-multi-select-grouped-modal__header">
          <h1 className="uup-multi-select-grouped-modal__header-title">Add {title}</h1>
        </header>
        <section className="uup-multi-select-grouped-modal__main-section">
          <aside className="uup-multi-select-grouped-modal__menu flex flex-col h-full min-h-0">
            <div>
              <Input
                theme={INPUT_THEME.SEARCH}
                placeholder="Search groups..."
                className="uup-multi-select-grouped-modal__header-search-input mb-4 sticky left-0 top-0 z-10"
                value={groupSearchText}
                onChange={handleGroupSearchChange}
              />
            </div>
            <div className="overflow-y-auto flex-1">
              <For
                each="category"
                of={groupOptionsLabeled.filter((category) =>
                  allFlagSelected ? category.value === 'all' : true
                )}
              >
                <Button
                  theme={BUTTON_THEME.GHOST}
                  className={classnames(
                    {
                      'uup-multi-select-grouped-modal__menu-selected-category':
                        category.value === selectedCategoryValue,
                    },
                    'uup-multi-select-grouped-modal__menu-category'
                  )}
                  key={category.value}
                  onClick={() => handleSelectedGroupChange(category)}
                  data-id={category.value}
                >
                  {category?.label}
                  <If
                    condition={
                      category?.value !== 'all' &&
                      countSelectedOptionsByCategory(category?.value).length
                    }
                  >
                    <div className="indicator">
                      {countSelectedOptionsByCategory(category?.value).length}
                    </div>
                  </If>
                </Button>
              </For>
            </div>
          </aside>
          <div className="uup-multi-select-grouped-modal__attributes">
            <div className="m-2">
              <Input
                theme={INPUT_THEME.SEARCH}
                placeholder="Search options..."
                className="uup-multi-select-grouped-modal__header-search-input"
                classes={{ inputContainer: 'uup-multi-select-grouped-modal__header-search' }}
                value={searchText}
                onChange={handleSearchChange}
              />
            </div>
            <div className="uup-multi-select-grouped-modal__attributes-list">
              <div className="flex flex-row items-center gap-2">
                <If condition={selectedCategoryValue !== 'all'}>
                  <CheckBox
                    classes={{
                      root: 'mb-4 ml-8',
                      label: 'text-lg',
                    }}
                    label={`Select All (${selectedCategoryOptionsCount || 0} of ${
                      optionsGrouped?.[selectedCategoryValue]?.length || 0
                    })`}
                    checked={
                      selectedCategoryOptionsCount ===
                      optionsGrouped?.[selectedCategoryValue]?.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </If>
                <If condition={!hideAllFlag && selectedCategoryValue === 'all'}>
                  <div className="ml-8 flex flex-col gap-0">
                    <CheckBox
                      classes={{
                        root: 'mb-4',
                        label: 'text-lg',
                      }}
                      label="All Flag"
                      checked={allFlagSelected}
                      onChange={(e) => handleAllFlag(e.target.checked)}
                    />
                    <If condition={allFlagSelected}>
                      <div className="text-neutral-500">{`Uncheck "All flag" to be able to select values`}</div>
                    </If>
                  </div>
                </If>
              </div>
              <If condition={!allFlagSelected && currentOptions?.length}>
                <For each="currentOption" of={currentOptions}>
                  <Card
                    key={extractValue(currentOption)}
                    theme={CARD_THEME.NO_SHADOW}
                    className="uup-multi-select-grouped-modal__attributes-card"
                  >
                    <CheckBox
                      data-value={extractValue(currentOption)}
                      label={extractLabel(currentOption)}
                      classes={{
                        root: `uup-multi-select-grouped-modal__checkbox-size`,
                        label: currentSelectedOptions.find(
                          (option) => extractValue(option) === extractValue(currentOption)
                        )
                          ? 'uup-multi-select-grouped-modal__selected-checkbox'
                          : '',
                      }}
                      checked={currentSelectedOptions.find(
                        (option) => extractValue(option) === extractValue(currentOption)
                      )}
                      onChange={(event) => {
                        handleOptionSelection(
                          currentOptions.find((currentOption) => {
                            return extractValue(currentOption) === event.currentTarget.dataset.value
                          })
                        )
                      }}
                    />
                    <If condition={showValue}>
                      <p className="uup-multi-select-grouped-modal__attributes-card-description">
                        {extractValue(currentOption)}
                      </p>
                    </If>
                  </Card>
                </For>
                <If condition={currentOptions.length >= PAGE_SIZE}>
                  <div className="pt-2 text-gray-500">
                    More options are available. Use the pagination control.
                  </div>
                </If>
              </If>
              <If condition={!currentOptions?.length && searchText}>
                <span className={'uup-multi-select-grouped-modal__empty-message'}>
                  {'Nothing found. Please try again.'}
                </span>
              </If>
              <If condition={!currentOptions?.length && !searchText}>
                <span className={'uup-multi-select-grouped-modal__empty-message'}>
                  No options available.
                </span>
              </If>
              <div className="p-2 sticky bottom-0 right-0 flex justify-end">
                <If condition={!allFlagSelected && currentOptions.length >= PAGE_SIZE}>
                  <Pagination
                    className="bg-white w-fit"
                    options={paginationOptionsLabeled}
                    page={offset / PAGE_SIZE}
                    onPageChange={handlePageChange}
                  />
                </If>
              </div>
            </div>
          </div>
        </section>

        <footer className="uup-multi-select-grouped-modal__footer">
          <span>
            <Choose>
              <When condition={allFlagSelected}>All flag selected</When>
              <Otherwise>
                {currentSelectedOptions?.length} selected of {optionsGrouped.all?.length} total
              </Otherwise>
            </Choose>
          </span>
          <div className="uup-multi-select-grouped-modal__footer-actions">
            <If condition={!hideShowValue && !allFlagSelected}>
              <CheckBox
                checked={showValue}
                onChange={handleShowValue}
                label="Show Value"
                classes={{
                  root: 'uup-multi-select-grouped-modal__checkbox-size',
                  label: showValue
                    ? 'uup-multi-select-grouped-modal__footer-checked-checkbox'
                    : 'uup-multi-select-grouped-modal__footer-checkbox',
                }}
              />
            </If>
            <If condition={!allFlagSelected}>
              <CheckBox
                checked={showOnlySelected}
                onChange={handleShowSelected}
                label="Show Only Selected"
                classes={{
                  root: 'uup-multi-select-grouped-modal__checkbox-size',
                  label: showOnlySelected
                    ? 'uup-multi-select-grouped-modal__footer-checked-checkbox'
                    : 'uup-multi-select-grouped-modal__footer-checkbox',
                }}
              />
            </If>
            <If condition={!hideShowOnlySelectedGroups}>
              <CheckBox
                checked={showOnlySelectedGroups}
                onChange={handleShowSelectedGroups}
                label="Show Only Selected Groups"
                classes={{
                  root: 'uup-multi-select-grouped-modal__checkbox-size',
                  label: showOnlySelectedGroups
                    ? 'uup-multi-select-grouped-modal__footer-checked-checkbox'
                    : 'uup-multi-select-grouped-modal__footer-checkbox',
                }}
              />
            </If>
            <Button isLoading={loading} onClick={handleSubmit}>
              Save Changes
            </Button>
          </div>
        </footer>
      </section>
    </Modal>
  )
}

MultiSelectGroupedModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  loading: PropTypes.bool,
  availableOptionsGrouped: PropTypes.object,
  selectedOptions: PropTypes.array,
  extractValue: PropTypes.func,
  extractLabel: PropTypes.func,
  extractGroupValue: PropTypes.func,
  onSubmit: PropTypes.func,
  hideAllFlag: PropTypes.bool,
  hideShowValue: PropTypes.bool,
  hideShowOnlySelectedGroups: PropTypes.bool,
}

export default React.memo(MultiSelectGroupedModal)
